import { Box, Button, Tooltip } from '@mui/material';
import usePermission from '../../../security/hook/usePermission';
import { TableAction } from '../types/TableTypes';

interface DataTableActionsProps<T> {
    row: T;
    actions: TableAction<T>[];
}

export const DataTableActions = <T extends Record<string, any>>({
    row,
    actions,
}: DataTableActionsProps<T>): JSX.Element => {
    const { hasPermission } = usePermission();

    const visibleActions = actions.filter((action) => {
        // Check if action should be hidden
        if (action.hidden && action.hidden(row)) {
            return false;
        }

        // Check permissions if required
        if (action.requirePermission) {
            return hasPermission(
                action.requirePermission.resource,
                action.requirePermission.action
            );
        }

        return true;
    });

    if (visibleActions.length === 0) {
        return <></>;
    }

    return (
        <Box
            sx={{
                display: 'flex',
                gap: 0.5,
                flexWrap: 'wrap',
            }}
        >
            {visibleActions.map((action, index) => {
                const isDisabled = action.disabled ? action.disabled(row) : false;

                const button = (
                    <Button
                        key={index}
                        variant={action.variant || 'outlined'}
                        color={action.color || 'primary'}
                        size="small"
                        disabled={isDisabled}
                        onClick={() => action.onClick(row)}
                        startIcon={action.icon}
                        sx={{
                            minWidth: 'auto',
                            textTransform: 'none',
                            fontWeight: 500,
                            fontSize: '0.75rem', // Smaller font
                            px: 1, // Tighter horizontal padding
                            py: 0.25, // Tighter vertical padding
                            '& .MuiButton-startIcon': {
                                marginRight: 0.5, // Reduce icon spacing
                            },
                        }}
                    >
                        {action.label}
                    </Button>
                );

                // Wrap in tooltip if disabled to show why
                if (isDisabled) {
                    return (
                        <Tooltip key={index} title="Action not available">
                            <span>{button}</span>
                        </Tooltip>
                    );
                }

                return button;
            })}
        </Box>
    );
};

